import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import './AdminDashboard.css';

function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadData();

        // Rafraichir la sante toutes les 60 secondes
        const interval = setInterval(() => {
            adminApi.getHealth()
                .then(res => setHealth(res.data))
                .catch(console.error);
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsRes, healthRes] = await Promise.all([
                adminApi.getStats(),
                adminApi.getHealth()
            ]);
            setStats(statsRes.data);
            setHealth(healthRes.data);
        } catch (err) {
            setError('Erreur lors du chargement des donnees');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getHealthStatusClass = (status) => {
        switch (status) {
            case 'healthy': return 'healthy';
            case 'warning': return 'warning';
            case 'critical': return 'critical';
            default: return '';
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-loading-inline">Chargement...</div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout>
                <div className="admin-error">{error}</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-dashboard">
                <div className="admin-page-header">
                    <h1>Dashboard</h1>
                    <p>Vue d'ensemble de la plateforme Signly</p>
                </div>

                {/* Stats Cards */}
                <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                        <div className="stat-icon blue">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div className="stat-value">{stats?.organizations?.total || 0}</div>
                        <div className="stat-label">Organisations</div>
                        <div className="stat-change positive">+{stats?.organizations?.new_30_days || 0} ce mois</div>
                    </div>

                    <div className="admin-stat-card">
                        <div className="stat-icon green">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                            </svg>
                        </div>
                        <div className="stat-value">{stats?.users?.total || 0}</div>
                        <div className="stat-label">Utilisateurs</div>
                        <div className="stat-change">{stats?.users?.active_7_days || 0} actifs cette semaine</div>
                    </div>

                    <div className="admin-stat-card">
                        <div className="stat-icon purple">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                        </div>
                        <div className="stat-value">{stats?.signatures?.total || 0}</div>
                        <div className="stat-label">Signatures</div>
                        <div className="stat-change positive">+{stats?.signatures?.new_30_days || 0} ce mois</div>
                    </div>

                    <div className="admin-stat-card">
                        <div className={`stat-icon ${health?.status === 'healthy' ? 'green' : 'orange'}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                            </svg>
                        </div>
                        <div className="stat-value">{health?.status === 'healthy' ? 'OK' : 'Alerte'}</div>
                        <div className="stat-label">Serveur</div>
                        <div className="stat-change">Load: {health?.load_average?.['1min']?.toFixed(2) || 'N/A'}</div>
                    </div>
                </div>

                <div className="dashboard-grid">
                    {/* Recent Organizations */}
                    <div className="admin-card">
                        <div className="admin-card-header">
                            <h2 className="admin-card-title">Organisations recentes</h2>
                            <Link to="/admin/organizations" className="admin-btn admin-btn-secondary">
                                Voir tout
                            </Link>
                        </div>
                        {stats?.recent_organizations?.length > 0 ? (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Nom</th>
                                        <th>Utilisateurs</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recent_organizations.map(org => (
                                        <tr key={org.id}>
                                            <td>
                                                <Link to={`/admin/organizations/${org.id}`} className="org-link">
                                                    {org.name}
                                                </Link>
                                            </td>
                                            <td>{org.user_count}</td>
                                            <td>{new Date(org.created_at).toLocaleDateString('fr-FR')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="no-data">Aucune organisation recente</p>
                        )}
                    </div>

                    {/* Server Health */}
                    <div className="admin-card">
                        <div className="admin-card-header">
                            <h2 className="admin-card-title">Sante du serveur</h2>
                            <span className={`admin-status ${getHealthStatusClass(health?.status)}`}>
                                {health?.status || 'Inconnu'}
                            </span>
                        </div>
                        <div className="health-grid">
                            <div className="health-item">
                                <span className="health-label">PHP</span>
                                <span className="health-value">{health?.php?.version}</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">MySQL</span>
                                <span className="health-value">{health?.mysql?.version?.split('-')[0]}</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Memoire</span>
                                <span className="health-value">{formatBytes(health?.memory?.usage || 0)}</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Disque</span>
                                <span className="health-value">{health?.disk?.usage_percent?.toFixed(1)}%</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Connexions DB</span>
                                <span className="health-value">{health?.mysql?.connections || 0}</span>
                            </div>
                            <div className="health-item">
                                <span className="health-label">Uptime DB</span>
                                <span className="health-value">
                                    {health?.mysql?.uptime_seconds
                                        ? Math.floor(health.mysql.uptime_seconds / 86400) + 'j'
                                        : 'N/A'}
                                </span>
                            </div>
                        </div>
                        {health?.warnings?.length > 0 && (
                            <div className="health-warnings">
                                {health.warnings.map((warning, index) => (
                                    <div key={index} className="health-warning">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        {warning}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent Activity */}
                    <div className="admin-card full-width">
                        <div className="admin-card-header">
                            <h2 className="admin-card-title">Activite recente</h2>
                        </div>
                        {stats?.recent_activity?.length > 0 ? (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Utilisateur</th>
                                        <th>Organisation</th>
                                        <th>Derniere connexion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recent_activity.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.first_name} {user.last_name}</td>
                                            <td>{user.organization_name || '-'}</td>
                                            <td>{user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="no-data">Aucune activite recente</p>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminDashboard;
